import { useState } from "react";

import { FeatureFlag } from "@vega-api/extended/auth/useFeatureFlag";
import { type NotificationResponse } from "@vega-api/generated/types/notificationResponse";
import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { NotificationCenter } from "@vega-core/components/NotificationCenter/NotificationCenter";
import { m } from "@vega-core/paraglide/messages";
import { isGPCoInvestPortal } from "@vega-core/utils/global";
import { useFeatureFlagGuard } from "@vega-modules/hooks/useFeatureFlagGuard";
import { CommonPaths } from "@vega-modules/paths";
import { CoInvestNotificationsList } from "@vega-modules/scenes/CoInvestments/components/CoInvestNotificationsList/CoInvestNotificationsList";
import { COINVEST_NOTIFICATION_PUBLISHER } from "@vega-modules/scenes/CoInvestments/constants/notificationConstants";
import { useNotificationCenterData } from "@vega-modules/scenes/Notifications/useNotificationCenterData";

import { CoInvestmentPageHeader } from "../components/CoInvestmentPageHeader/CoInvestmentPageHeader";
import { NotificationsDealFilterDropdown } from "../components/NotificationsDealFilterDropdown/NotificationsDealFilterDropdown";

export const CoInvestNotificationsPage = () => {
  useFeatureFlagGuard(
    FeatureFlag.COINVEST_NOTIFICATIONS,
    isGPCoInvestPortal ? undefined : CommonPaths.Home,
  );

  const [selectedDealId, setSelectedDealId] = useState<string>();

  const notificationCenterProps = useNotificationCenterData(
    {
      publisher: COINVEST_NOTIFICATION_PUBLISHER,
      ...(selectedDealId ? { metadata: { dealId: selectedDealId } } : {}),
    },
    { readVisibleItemsOnly: true },
  );

  const hasUnread = notificationCenterProps.items.some((item) => !item.readAt);

  return (
    <div className="mt-1 pb-52">
      <CoInvestmentPageHeader
        action={
          <div className="flex items-center gap-4">
            {hasUnread ? (
              <ThemedButton
                dataTestId="coinvest-notifications-mark-all-read"
                onClick={() => notificationCenterProps.onMarkAllRead()}
                variant="text"
              >
                {m.notificationCenter_markAllRead()}
              </ThemedButton>
            ) : null}
            <NotificationsDealFilterDropdown
              onChange={setSelectedDealId}
              value={selectedDealId}
            />
          </div>
        }
        showDrawer={false}
        title={m.coInvestments_notifications_title()}
      />

      <div className="mt-6">
        <NotificationCenter<NotificationResponse> {...notificationCenterProps}>
          <CoInvestNotificationsList
            emptyDescription={m.coInvestments_notifications_empty_subtitle()}
            emptyTitle={m.coInvestments_notifications_empty_title()}
          />
        </NotificationCenter>
      </div>
    </div>
  );
};
